import type { QueryResult, ResolverResult } from "./types";

export function mean(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

export function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

export function percentile95(values: number[]): number | undefined {
  if (values.length < 2) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil(0.95 * sorted.length) - 1;
  return sorted[Math.max(0, Math.min(sorted.length - 1, index))];
}

export function successRate(results: QueryResult[]): number | undefined {
  if (results.length === 0) return undefined;
  return (
    results.filter((result) => result.status === "success" || result.status === "opaque").length /
    results.length
  );
}

export function aggregateResult(result: ResolverResult): ResolverResult {
  const latencies = result.queryResults
    .filter(
      (query) =>
        (query.status === "success" || query.status === "opaque") &&
        typeof query.durationMs === "number"
    )
    .map((query) => query.durationMs as number);

  return {
    ...result,
    minMs: latencies.length ? Math.min(...latencies) : undefined,
    medianMs: median(latencies),
    averageMs: mean(latencies),
    p95Ms: percentile95(latencies),
    maxMs: latencies.length ? Math.max(...latencies) : undefined,
    successRate: successRate(result.queryResults)
  };
}
